/**
 * API helpers for the global AI Assistant chat widget.
 */
import axios from "axios";
import { API } from "./client";

export const newAssistantSession = () => axios.post(`${API}/assistant/chat/new`);

/**
 * Send a message to the AI Assistant with full page context.
 *
 * @param {string} sessionId - The chat session ID
 * @param {string} message - The user's message text
 * @param {string|null} projectId - Legacy project_id (backward compat)
 * @param {object|null} pageContext - Full page context from the current route
 * @param {string} pageContext.route - Current URL path (e.g. "/projects/abc123")
 * @param {string} pageContext.page_type - Page type identifier (e.g. "project_detail")
 * @param {string|null} pageContext.project_id - Project ID if on a project page
 * @param {string|null} pageContext.project_name - Project name if known
 * @param {string|null} pageContext.folder_id - OneDrive folder ID if on Explore Files
 * @param {string|null} pageContext.folder_path - Breadcrumb path if on Explore Files
 */
export const sendAssistantMessage = (sessionId, message, projectId, pageContext) =>
  axios.post(`${API}/assistant/chat`, {
    session_id: sessionId,
    message,
    project_id: projectId || undefined,
    page_context: pageContext || undefined,
  });

export const getAssistantHistory = (sessionId) => axios.get(`${API}/assistant/chat/${sessionId}`);

/** Fetch the agent capability registry (names, scopes, descriptions). */
export const getAssistantAgents = () => axios.get(`${API}/assistant/agents`);

// ── F-051: Session management ──────────────────────────────────────────────

/** List all sessions with titles, folders, dates. */
export const listSessions = () => axios.get(`${API}/assistant/sessions`);

/** Update session metadata (title, folder). */
export const updateSession = (sessionId, data) =>
  axios.put(`${API}/assistant/sessions/${sessionId}`, data);

/** Delete an assistant chat session. */
export const deleteAssistantSession = (sessionId) => axios.delete(`${API}/assistant/chat/${sessionId}`);

/** Create a folder for organizing chats. */
export const createFolder = (name) =>
  axios.post(`${API}/assistant/sessions/folder`, { name });

/** Delete a folder (moves chats to root). */
export const deleteFolder = (name) =>
  axios.delete(`${API}/assistant/sessions/folder/${encodeURIComponent(name)}`);

// ── F-051: Sub-agent management ────────────────────────────────────────────

/** Get running sub-agents for a session. */
export const getSubAgents = (sessionId) =>
  axios.get(`${API}/assistant/sub-agents/${sessionId}`);

/** Run a queued sub-agent in a request independent of the main chat. */
export const runSubAgent = (subAgentId) =>
  axios.post(`${API}/assistant/sub-agents/${subAgentId}/run`);

/** Force stop a running sub-agent. */
export const stopSubAgent = (subAgentId) =>
  axios.post(`${API}/assistant/sub-agents/${subAgentId}/stop`);

/** Get pending permission requests for a session. */
export const getPermissions = (sessionId) =>
  axios.get(`${API}/assistant/sub-agents/${sessionId}/permissions`);

/** Respond to a sub-agent permission request. */
export const resolvePermission = (requestId, approved) =>
  axios.post(`${API}/assistant/sub-agents/permissions/${requestId}`, { approved });
